import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { Check, ChevronDown, GripVertical, MoreVertical, ShieldCheck } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { useAuth } from "./auth";
import { stagesApi, type Stage } from "./contacts-api";
import { stageIcons } from "./lifecycle-meta";
import { navLabel, settingsNav as lifecycleNav, settingsNavAnchors as lifecycleNavAnchors } from "./settings-nav";
import { apiErrorText } from "@/lib/api";
import { goAnchor } from "./app-nav";
import { t, tn } from "@/lib/i18n";

/* «Жизненный цикл»: этапы воронки слева, потерянные этапы справа — с сервера (/api/lifecycle).
   Правки копятся на экране и уходят на сервер по «Сохранить»: новые этапы, названия, иконки, этап по умолчанию,
   «выигран», перенос в потерянные, порядок и удаление. Менять этапы могут только владелец и администраторы. */

type Side = "main" | "lost";

const isNew = (id: string) => id.startsWith("new-");
const clone = (list: Stage[]) => list.map((s) => ({ ...s }));
const snap = (main: Stage[], lost: Stage[]) => JSON.stringify({ main, lost });
const INPUT =
  "h-9 w-full box-border min-w-0 rounded-btn border border-border bg-background px-2.5 text-[12.5px] font-semibold text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

const contacts = (n: number) => tn(n, "контакт", "контакта", "контактов");

function LifecycleDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const { user } = useAuth();
  const canEdit = user?.role === "owner" || user?.role === "admin";
  const [server, setServer] = useState<Stage[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [lost, setLost] = useState<Stage[]>([]);
  const [saved, setSaved] = useState(() => snap([], []));
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showStages, setShowStages] = useState(true);
  const [openDesc, setOpenDesc] = useState<string[]>([]);
  const [menu, setMenu] = useState<{ id: string; side: Side; top: number; left: number } | null>(null);
  const [drag, setDrag] = useState<{ id: string; side: Side } | null>(null);
  const [over, setOver] = useState("");
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = useCallback((text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4200);
  }, []);

  // список с сервера → активные и потерянные этапы на экране
  const apply = useCallback((list: Stage[]) => {
    const main = list.filter((s) => !s.lost);
    const gone = list.filter((s) => s.lost);
    setServer(list);
    setStages(clone(main));
    setLost(clone(gone));
    setSaved(snap(main, gone));
    setLoaded(true);
  }, []);

  const load = useCallback(async () => {
    try {
      apply(await stagesApi.list());
    } catch (err) {
      setLoaded(true);
      flash(apiErrorText(err));
    }
  }, [apply, flash]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if ((e.target as HTMLElement | null)?.closest?.("[data-menu-root]")) return;
      setMenu(null);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [menu]);

  const dirty = useMemo(() => snap(stages, lost) !== saved, [stages, lost, saved]);

  const listOf = (side: Side) => (side === "main" ? stages : lost);
  const putList = (side: Side, next: Stage[]) => (side === "main" ? setStages(next) : setLost(next));

  const patch = (side: Side, id: string, data: Partial<Stage>) => {
    const next = listOf(side).map((s) => (s.id === id ? { ...s, ...data } : s));
    putList(side, next);
  };

  const toggleDesc = (id: string) => {
    setOpenDesc((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setMenu(null);
  };

  const addStage = (side: Side) => {
    const n = listOf(side).length + 1;
    const stage: Stage = {
      id: `new-${Date.now()}`,
      name: side === "main" ? t("Этап {n}", { n }) : t("Потерянный этап {n}", { n }),
      description: "",
      icon: side === "main" ? "ri-flag-line" : "ri-snowflake-line",
      lost: side === "lost",
      isDefault: false,
      isWon: false,
      position: n,
      count: 0,
    };
    putList(side, [...listOf(side), stage]);
    setOpenDesc((prev) => [...prev, stage.id]);
    flash(t("Этап «{name}» добавлен — задайте название и иконку, затем нажмите «Сохранить»", { name: stage.name }));
  };

  const makeDefault = (id: string) => {
    setStages((prev) => prev.map((s) => ({ ...s, isDefault: s.id === id })));
    setMenu(null);
    flash(t("После сохранения новые контакты будут попадать на этап «{name}»", { name: stages.find((s) => s.id === id)?.name ?? "" }));
  };

  const makeWon = (s: Stage) => {
    patch("main", s.id, { isWon: !s.isWon });
    setMenu(null);
    flash(s.isWon ? t("Этап «{name}» больше не считается выигранным", { name: s.name }) : t("Этап «{name}» помечен как выигранный", { name: s.name }));
  };

  const moveStage = (s: Stage, from: Side) => {
    setMenu(null);
    if (from === "main" && s.isDefault) {
      flash(t("Этап по умолчанию нельзя перенести в потерянные — сначала назначьте другой этап по умолчанию"));
      return;
    }
    if (from === "main") {
      setStages((prev) => prev.filter((x) => x.id !== s.id));
      setLost((prev) => [...prev, { ...s, lost: true, isWon: false }]);
      flash(t("Этап «{name}» перенесён в потерянные", { name: s.name }));
    } else {
      setLost((prev) => prev.filter((x) => x.id !== s.id));
      setStages((prev) => [...prev, { ...s, lost: false }]);
      flash(t("Этап «{name}» возвращён в воронку", { name: s.name }));
    }
  };

  const removeStage = (s: Stage, side: Side) => {
    const list = listOf(side);
    if (s.isDefault) {
      setMenu(null);
      flash(t("Этап по умолчанию удалить нельзя — сначала назначьте другой этап по умолчанию"));
      return;
    }
    if (side === "main" && list.length <= 1) {
      setMenu(null);
      flash(t("Последний этап удалить нельзя — в воронке должен остаться хотя бы один"));
      return;
    }
    putList(side, list.filter((x) => x.id !== s.id));
    setMenu(null);
    const def = stages.find((x) => x.isDefault)?.name ?? "";
    flash(
      s.count > 0
        ? t("Этап «{name}» удалён — после сохранения {contacts} перейдут на этап «{def}»", { name: s.name, contacts: contacts(s.count), def })
        : t("Этап «{name}» удалён — нажмите «Сохранить», чтобы применить", { name: s.name }),
    );
  };

  const cycleIcon = (s: Stage, side: Side) => {
    const i = stageIcons.indexOf(s.icon);
    const icon = stageIcons[(i + 1) % stageIcons.length];
    patch(side, s.id, { icon });
    setMenu(null);
  };

  const openMenu = (e: MouseEvent<HTMLButtonElement>, id: string, side: Side) => {
    const r = e.currentTarget.getBoundingClientRect();
    const width = 230;
    setMenu({
      id,
      side,
      top: Math.min(r.bottom + 4, window.innerHeight - 250),
      left: Math.max(8, Math.min(window.innerWidth - width - 8, r.right - width)),
    });
  };

  const dropOn = (side: Side, id: string) => {
    if (!drag || drag.side !== side || drag.id === id) {
      setDrag(null);
      setOver("");
      return;
    }
    const list = listOf(side);
    const from = list.findIndex((s) => s.id === drag.id);
    const to = list.findIndex((s) => s.id === id);
    if (from < 0 || to < 0) return;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    putList(side, next);
    setDrag(null);
    setOver("");
  };

  // правки уходят на сервер по шагам: новые этапы → этап по умолчанию → остальные изменения → удаление → порядок
  const save = async () => {
    const all = [...stages.map((s) => ({ ...s, lost: false })), ...lost.map((s) => ({ ...s, lost: true }))];
    if (all.some((s) => !s.name.trim())) {
      flash(t("У каждого этапа должно быть название"));
      return;
    }
    const names = all.map((s) => s.name.trim().toLowerCase());
    const twin = names.find((n, i) => names.indexOf(n) !== i);
    if (twin) {
      flash(t("Два этапа называются «{name}» — переименуйте один из них", { name: all[names.indexOf(twin)].name.trim() }));
      return;
    }
    setBusy(true);
    try {
      let list = server;
      const real = new Map<string, string>();
      const idOf = (id: string) => real.get(id) ?? id;
      for (const s of all.filter((x) => isNew(x.id))) {
        const before = new Set(list.map((x) => x.id));
        list = await stagesApi.create({ name: s.name.trim(), description: s.description.trim(), icon: s.icon, lost: s.lost });
        const made = list.find((x) => !before.has(x.id));
        if (made) real.set(s.id, made.id);
      }
      const diff = (s: Stage) => {
        const was = list.find((x) => x.id === idOf(s.id));
        if (!was) return null;
        const body: Parameters<typeof stagesApi.update>[1] = {};
        if (s.name.trim() !== was.name) body.name = s.name.trim();
        if (s.description.trim() !== was.description) body.description = s.description.trim();
        if (s.icon !== was.icon) body.icon = s.icon;
        if (s.lost !== was.lost) body.lost = s.lost;
        if (s.isWon !== was.isWon) body.isWon = s.isWon;
        if (s.isDefault && !was.isDefault) body.isDefault = true;
        return Object.keys(body).length ? body : null;
      };
      // этап по умолчанию — первым, иначе сервер не даст перенести прежний в потерянные или удалить его
      const def = all.find((s) => s.isDefault);
      const ordered = def ? [def, ...all.filter((s) => s !== def)] : all;
      for (const s of ordered) {
        const body = diff(s);
        if (body) list = await stagesApi.update(idOf(s.id), body);
      }
      const keep = new Set(all.map((s) => idOf(s.id)));
      for (const s of list.filter((x) => !keep.has(x.id))) list = await stagesApi.remove(s.id);
      list = await stagesApi.reorder(stages.map((s) => idOf(s.id)));
      list = await stagesApi.reorder(lost.map((s) => idOf(s.id)));
      apply(list);
      setOpenDesc([]);
      flash(t("Этапы сохранены — воронка контактов уже использует новый порядок"));
    } catch (err) {
      // часть шагов могла пройти — показываем то, что на сервере
      await load();
      flash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    const back = JSON.parse(saved) as { main: Stage[]; lost: Stage[] };
    setStages(clone(back.main));
    setLost(clone(back.lost));
    setOpenDesc([]);
    setMenu(null);
    flash(t("Изменения отменены — этапы вернулись к сохранённому виду"));
  };

  const onNav = (id: string) => {
    if (id === "lifecycle") return;
    const anchor = lifecycleNavAnchors[id];
    if (anchor) {
      goAnchor(anchor);
      return;
    }
    flash(t("Раздел «{label}» пока не подключён", { label: navLabel(id) }));
  };

  const menuItem = (s: Stage) => {
    if (!menu) return null;
    const side = menu.side;
    return (
      <div
        role="menu"
        data-menu-root
        style={{ top: menu.top, left: menu.left }}
        className="fixed z-50 w-[230px] overflow-hidden rounded-btn border border-border bg-card p-1 shadow-lift"
      >
        <button
          type="button"
          role="menuitem"
          onClick={() => cycleIcon(s, side)}
          className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition hover:bg-muted"
        >
          <i className="ri-refresh-line text-[14px] leading-none text-muted-foreground" aria-hidden />
          {t("Сменить иконку")}
        </button>
        {side === "main" ? (
          <>
            <button
              type="button"
              role="menuitem"
              onClick={() => makeDefault(s.id)}
              disabled={s.isDefault}
              className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-flag-2-line text-[14px] leading-none text-muted-foreground" aria-hidden />
              {t("Сделать по умолчанию")}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => makeWon(s)}
              className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition hover:bg-muted"
            >
              <i className="ri-trophy-line text-[14px] leading-none text-muted-foreground" aria-hidden />
              {s.isWon ? t("Снять отметку «выигран»") : t("Пометить как выигранный")}
            </button>
          </>
        ) : null}
        <button
          type="button"
          role="menuitem"
          onClick={() => moveStage(s, side)}
          className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition hover:bg-muted"
        >
          <i className="ri-arrow-left-right-line text-[14px] leading-none text-muted-foreground" aria-hidden />
          {side === "main" ? t("В потерянные этапы") : t("Вернуть в воронку")}
        </button>
        <button
          type="button"
          role="menuitem"
          onClick={() => removeStage(s, side)}
          className="flex w-full items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium text-red-500 transition hover:bg-red-500/10"
        >
          <i className="ri-delete-bin-line text-[14px] leading-none" aria-hidden />
          {t("Удалить этап")}
        </button>
      </div>
    );
  };

  const stageRow = (s: Stage, side: Side, index: number) => {
    const open = openDesc.includes(s.id);
    return (
      <div
        key={s.id}
        draggable={canEdit}
        onDragStart={() => setDrag({ id: s.id, side })}
        onDragEnd={() => {
          setDrag(null);
          setOver("");
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (over !== s.id) setOver(s.id);
        }}
        onDrop={(e) => {
          e.preventDefault();
          dropOn(side, s.id);
        }}
        className={cn(
          "rounded-btn border bg-background/60 p-2.5 transition",
          over === s.id && drag && drag.id !== s.id ? "border-primary" : "border-border",
          drag?.id === s.id && "opacity-60",
        )}
      >
        <div className="flex items-center gap-2">
          {canEdit ? (
            <span className="flex-none cursor-grab text-muted-foreground/60 active:cursor-grabbing" aria-hidden>
              <GripVertical className="size-4" />
            </span>
          ) : null}
          <span
            className={cn(
              "grid size-8 flex-none place-items-center rounded-btn text-base leading-none",
              side === "lost" ? "bg-amber-400/15 text-amber-500" : "bg-primary/12 text-primary",
            )}
            aria-hidden
          >
            <i className={s.icon} />
          </span>
          <label className="min-w-0 flex-1">
            <span className="sr-only">{t("Название этапа {n}", { n: index + 1 })}</span>
            <input
              value={s.name}
              onChange={(e) => patch(side, s.id, { name: e.target.value })}
              readOnly={!canEdit}
              maxLength={48}
              className={INPUT}
            />
          </label>
          {s.isDefault ? (
            <span className="hidden flex-none whitespace-nowrap rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary sm:inline-flex">
              {t("Этап по умолчанию")}
            </span>
          ) : null}
          {s.isWon ? (
            <span className="hidden flex-none whitespace-nowrap rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-500 sm:inline-flex">
              {t("Выигран")}
            </span>
          ) : null}
          {canEdit ? (
            <div className="relative flex-none" data-menu-root>
              <button
                type="button"
                onClick={(e) => (menu?.id === s.id ? setMenu(null) : openMenu(e, s.id, side))}
                aria-label={t("Действия с этапом {name}", { name: s.name })}
                aria-expanded={menu?.id === s.id}
                className="grid size-7 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <MoreVertical className="size-4" aria-hidden />
              </button>
            </div>
          ) : null}
        </div>

        <button
          type="button"
          onClick={() => toggleDesc(s.id)}
          aria-expanded={open}
          className="mt-1.5 flex cursor-pointer items-center gap-1 ps-8 text-[11px] font-medium text-muted-foreground transition hover:text-foreground"
        >
          {open ? t("Скрыть описание") : t("Показать описание")}
          <ChevronDown className={cn("size-3 transition", open && "rotate-180")} aria-hidden />
        </button>

        {open ? (
          <label className="mt-1.5 block ps-8">
            <span className="sr-only">{t("Описание этапа {name}", { name: s.name })}</span>
            <textarea
              value={s.description}
              onChange={(e) => patch(side, s.id, { description: e.target.value })}
              readOnly={!canEdit}
              rows={2}
              maxLength={160}
              placeholder={t("Что происходит с контактом на этом этапе")}
              className="w-full box-border resize-none rounded-btn border border-border bg-background px-2.5 py-1.5 text-[11.5px] leading-relaxed text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
          </label>
        ) : null}
      </div>
    );
  };

  const menuStage = menu ? listOf(menu.side).find((s) => s.id === menu.id) : null;

  return (
    <Band id="lifecycle" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} {t("/ настройки → входящие → жизненный цикл")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {loaded ? t("Этапов: {n}", { n: stages.length + lost.length }) : t("Загружаем этапы…")}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {lifecycleNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "lifecycle";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onNav(it.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <ShieldCheck className="size-3.5" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Менять этапы могут владелец и администраторы. Остальные сотрудники переводят контакты между этапами.")}
              </p>
            </div>
          </div>

          {/* ── этапы ── */}
          <div className="min-w-0 p-3.5 md:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold leading-snug">{t("Жизненный цикл")}</h3>
                <p className="mt-1 max-w-xl text-[12px] leading-relaxed text-muted-foreground">
                  {t("Отслеживайте, на каком этапе продаж находится каждый контакт.")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => flash(t("Новые контакты попадают на этап по умолчанию. «Выигран» и потерянные этапы считаются в отчётах воронки."))}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[12px] font-semibold text-primary transition hover:opacity-80"
              >
                <i className="ri-book-open-line text-sm leading-none" aria-hidden />
                {t("Узнать больше")}
              </button>
            </div>

            <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
              {lifecycleNav.flatMap((g) => g.items).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onNav(it.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    it.id === "lifecycle" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <i className={it.icon} aria-hidden />
                  {it.label}
                </button>
              ))}
            </div>

            {/* тумблер показа этапов */}
            <div className="mt-4 flex items-start gap-3 rounded-card border border-border bg-background/40 p-3.5">
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-semibold">{t("Показывать этапы жизненного цикла")}</p>
                <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                  {t("Этапы видны во входящих и в карточке контакта. Если скрыть, данные контактов останутся прежними — пропадёт только отображение.")}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={showStages}
                aria-label={t("Показывать этапы жизненного цикла")}
                onClick={() => {
                  setShowStages((v) => !v);
                  // пока только на этом экране: общей настройки показа этапов на сервере нет
                  flash(showStages ? t("Этапы скрыты на этом экране — контакты и данные не изменились") : t("Этапы снова видны"));
                }}
                className={cn(
                  "relative mt-0.5 h-6 w-11 flex-none cursor-pointer rounded-full transition",
                  showStages ? "bg-primary" : "bg-foreground/20",
                )}
              >
                <span
                  className={cn(
                    "absolute top-0.5 size-5 rounded-full bg-white transition-all",
                    showStages ? "start-[1.375rem]" : "start-0.5",
                  )}
                  aria-hidden
                />
              </button>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <h4 className="text-[13px] font-semibold">{t("Настроить этапы воронки")}</h4>
              {canEdit ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={cancel}
                    disabled={!dirty || busy}
                    className="inline-flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-3.5 text-[12px] font-semibold text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {t("Отмена")}
                  </button>
                  <button
                    type="button"
                    onClick={() => void save()}
                    disabled={!dirty || busy}
                    className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Check className="size-3.5" aria-hidden />
                    {busy ? t("Сохраняем…") : t("Сохранить")}
                  </button>
                </div>
              ) : null}
            </div>

            {showStages ? (
              <div className="mt-3 grid gap-4 xl:grid-cols-2">
                {/* этапы воронки */}
                <div className="rounded-card border border-border bg-background/30 p-3 md:p-4">
                  <div className="flex items-start gap-2.5 rounded-btn bg-primary/10 p-3">
                    <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/15 text-base leading-none text-primary" aria-hidden>
                      <i className="ri-route-line" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[12.5px] font-semibold">{t("Этапы жизненного цикла")}</p>
                      <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                        {t("Ведите контакты по этапам продаж. Этап по умолчанию назначается новым контактам автоматически.")}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-col gap-2">
                    {stages.map((s, i) => (
                      <div key={s.id}>
                        <p className="mb-1 ps-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                          {t("Этап {n}", { n: i + 1 })}
                          {s.count > 0 ? ` · ${contacts(s.count)}` : ""}
                        </p>
                        {stageRow(s, "main", i)}
                      </div>
                    ))}
                  </div>

                  {canEdit ? (
                    <button
                      type="button"
                      onClick={() => addStage("main")}
                      className="mt-3 inline-flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn border border-dashed border-border text-[12px] font-semibold text-muted-foreground transition hover:border-primary hover:text-primary"
                    >
                      <i className="ri-add-line" aria-hidden />
                      {t("Добавить этап")}
                    </button>
                  ) : null}
                </div>

                {/* потерянные */}
                <div className="rounded-card border border-border bg-background/30 p-3 md:p-4">
                  <div className="flex items-start gap-2.5 rounded-btn bg-amber-400/10 p-3">
                    <span className="grid size-8 flex-none place-items-center rounded-btn bg-amber-400/15 text-base leading-none text-amber-500" aria-hidden>
                      <i className="ri-emotion-sad-line" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[12.5px] font-semibold">{t("Потерянные этапы")}</p>
                      <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                        {t("Сюда попадают контакты, вышедшие из процесса продаж. Отдельный ящик для них не создаётся.")}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-col gap-2">
                    {lost.map((s, i) => (
                      <div key={s.id}>
                        <p className="mb-1 ps-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                          {t("Потерянный этап {n}", { n: i + 1 })}
                          {s.count > 0 ? ` · ${contacts(s.count)}` : ""}
                        </p>
                        {stageRow(s, "lost", i)}
                      </div>
                    ))}
                  </div>

                  {canEdit ? (
                    <button
                      type="button"
                      onClick={() => addStage("lost")}
                      className="mt-3 inline-flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn border border-dashed border-border text-[12px] font-semibold text-muted-foreground transition hover:border-primary hover:text-primary"
                    >
                      <i className="ri-add-line" aria-hidden />
                      {t("Добавить этап")}
                    </button>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="mt-3 rounded-card border border-dashed border-border bg-background/30 px-4 py-10 text-center">
                <i className="ri-eye-off-line text-2xl leading-none text-muted-foreground" aria-hidden />
                <p className="mt-2 text-[12.5px] font-semibold">{t("Этапы скрыты")}</p>
                <p className="mx-auto mt-1 max-w-sm text-[11.5px] leading-relaxed text-muted-foreground">
                  {t("Воронка скрыта на этом экране. Включите тумблер выше, чтобы вернуть этапы и настроить их.")}
                </p>
              </div>
            )}

            <p className="mt-4 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
              {canEdit
                ? t("Изменения применяются после «Сохранить». Контакты удалённого этапа переходят на этап по умолчанию.")
                : t("У вас только просмотр: этапы меняют владелец и администраторы.")}
            </p>
          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {menu && menuStage ? menuItem(menuStage) : null}
      </div>
    </Band>
  );
}

export default LifecycleDemo;